import {useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import api from 'api';
import {Submit} from 'ui';
import {
  Overlay,
  Sheet,
  CloseButton,
  DetailTitle,
  DetailImage,
  DetailText,
  FileField,
  FileName,
  FieldHint,
  CommentField,
  FieldError,
  Confirmation,
} from './styles';

const MAX_SIZE_MESSAGE = 'Votre photo dépasse 10 Mo, merci d’en envoyer une plus légère.';

const readError = error => {
  try {
    const response = JSON.parse(error?.request?.response || '{}');
    if (response.message) return response.message;
  } catch {
    // Réponse illisible : message générique ci-dessous.
  }
  return 'Le transfert de la photo a échoué, merci de réessayer.';
};

// Dépôt de la photo de la carte dessert, avec les règles et les textes du cartouche de la
// page /recompense/ du site : une photo obligatoire, un commentaire facultatif.
const VisibilityProof = ({action, visibility, onClose, onSent}) => {
  const dispatch = useDispatch();
  const [photo, setPhoto] = useState(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const points = action.acf.actions_points;

  const handlePhoto = event => {
    const file = event.target.files && event.target.files[0];
    setError('');
    if (file && visibility.max_size && file.size > visibility.max_size) {
      setPhoto(null);
      setError(MAX_SIZE_MESSAGE);
      return;
    }
    setPhoto(file || null);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (sending) return;
    if (!photo) {
      setError('Veuillez joindre une photo de votre carte dessert.');
      return;
    }
    setSending(true);
    setError('');
    const {error: requestError} = await dispatch(
      api.postVisibilityProof({photo, comment: comment.trim()}),
    );
    setSending(false);
    if (requestError) {
      setError(readError(requestError));
      return;
    }
    setSent(true);
    onSent();
  };

  return (
    <Overlay onClick={onClose}>
      <Sheet role="dialog" aria-modal="true" onClick={event => event.stopPropagation()}>
        <CloseButton type="button" aria-label="Fermer" onClick={onClose} />
        {sent ? (
          <Confirmation role="status">
            Votre preuve a bien été envoyée. Votre commercial Carte d’Or va la vérifier&nbsp;: vos points
            seront crédités dès qu’elle sera validée.
          </Confirmation>
        ) : (
          <>
            <DetailTitle>Comment obtenir mes {points}&nbsp;points&nbsp;?</DetailTitle>
            {action.acf.actions_image && <DetailImage src={action.acf.actions_image} alt="" />}
            <DetailText>
              <ul>
                <li>Faites apparaître la marque Carte d’Or sur votre carte dessert.</li>
                <li>Photographiez la page de votre carte où la marque est visible.</li>
                <li>
                  Envoyez-nous la photo ci-dessous&nbsp;: vos {points} points sont crédités dès que votre
                  commercial Carte d’Or l’a validée.
                </li>
              </ul>
            </DetailText>
            <form onSubmit={handleSubmit} style={{width: '100%'}}>
              <FileField>
                Votre photo *
                <input type="file" accept="image/*" onChange={handlePhoto} />
              </FileField>
              {photo && <FileName>{photo.name}</FileName>}
              <FieldHint>
                Formats acceptés&nbsp;: {visibility.formats}. Poids maximum&nbsp;: 10&nbsp;Mo.
              </FieldHint>
              <CommentField
                placeholder="Par exemple : l’endroit de la carte où la marque apparaît."
                aria-label="Un commentaire à ajouter ? (facultatif)"
                value={comment}
                onChange={event => setComment(event.target.value)}
              />
              {error && <FieldError role="alert">{error}</FieldError>}
              <Submit color="white" disabled={sending} style={{width: '100%'}}>
                {sending ? 'Envoi…' : 'Envoyer ma preuve'}
              </Submit>
            </form>
          </>
        )}
      </Sheet>
    </Overlay>
  );
};

VisibilityProof.propTypes = {
  action: PropTypes.object.isRequired,
  visibility: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSent: PropTypes.func.isRequired,
};

export default VisibilityProof;
